import { create } from 'zustand';
import type { Timer, Room, SessionEntry, ActivityLogEntry, User } from '../types';

interface TimerStore {
  // User
  user: User | null;
  setUser: (user: User) => void;

  // Room
  currentRoom: Room | null;
  setCurrentRoom: (room: Room | null) => void;

  // Timers (synced from Firebase)
  timers: Record<string, Timer>;
  setTimers: (timers: Record<string, Timer>) => void;
  updateTimer: (id: string, updates: Partial<Timer>) => void;

  // History (synced from Firebase)
  history: Record<string, SessionEntry>;
  setHistory: (history: Record<string, SessionEntry>) => void;

  // Activity Log (synced from Firebase)
  activityLog: Record<string, ActivityLogEntry>;
  setActivityLog: (log: Record<string, ActivityLogEntry>) => void;

  // Participants
  participants: Record<string, { pseudo: string; color: string; online: boolean; lastSeen: number }>;
  setParticipants: (participants: TimerStore['participants']) => void;

  // Connection state
  isConnected: boolean;
  setIsConnected: (connected: boolean) => void;

  // UI state
  isCreateModalOpen: boolean;
  setCreateModalOpen: (open: boolean) => void;
  isHistoryPanelOpen: boolean;
  setHistoryPanelOpen: (open: boolean) => void;

  // Recent rooms (localStorage backed)
  recentRooms: Array<{ id: string; name: string; code: string; lastVisited: number }>;
  addRecentRoom: (room: { id: string; name: string; code: string }) => void;
}

export const useTimerStore = create<TimerStore>((set) => ({
  // User
  user: null,
  setUser: (user) => {
    localStorage.setItem('namm-user', JSON.stringify(user));
    set({ user });
  },

  // Room
  currentRoom: null,
  setCurrentRoom: (room) => set({ currentRoom: room }),

  // Timers
  timers: {},
  setTimers: (timers) => set({ timers }),
  updateTimer: (id, updates) =>
    set((state) => ({
      timers: {
        ...state.timers,
        [id]: { ...state.timers[id], ...updates },
      },
    })),

  // History
  history: {},
  setHistory: (history) => set({ history }),

  // Activity Log
  activityLog: {},
  setActivityLog: (activityLog) => set({ activityLog }),

  // Participants
  participants: {},
  setParticipants: (participants) => set({ participants }),

  // Connection
  isConnected: false,
  setIsConnected: (connected) => set({ isConnected: connected }),

  // UI
  isCreateModalOpen: false,
  setCreateModalOpen: (open) => set({ isCreateModalOpen: open }),
  isHistoryPanelOpen: false,
  setHistoryPanelOpen: (open) => set({ isHistoryPanelOpen: open }),

  // Recent rooms
  recentRooms: JSON.parse(localStorage.getItem('namm-recent-rooms') || '[]'),
  addRecentRoom: (room) =>
    set((state) => {
      const filtered = state.recentRooms.filter((r) => r.id !== room.id);
      const updated = [{ ...room, lastVisited: Date.now() }, ...filtered].slice(0, 10);
      localStorage.setItem('namm-recent-rooms', JSON.stringify(updated));
      return { recentRooms: updated };
    }),
}));
